/**
 * The component which is set on the p2p react overpanel and handles the theme selection for gift/theme flows.
 *
 * TIP: When used with the react overpanel, the overpanel should be set to the preset 'FULL' and should be given a
 * contentClassName override. That string override should contain a class that sets padding: 0 and has col-xs-12. This
 * is so the background gradient extends fully to the edges of the viewport.
 */

import { connect } from 'transfer/utils/p2p-connect'
import PropTypes from 'prop-types'
import React from 'react'
import { css } from 'lib/paypalphrodite'
import classNames from 'classnames'
import ThemeCard from '../theme-card'
import styles from './new-theme-selection.styles'
import { getThemeImage } from '../../utils/theme-utils'
import * as fpti from '../../../lib/analytics/fpti'
import content from 'pp-react-l10n'
let i18n = content('transfer/themes')
const pageGroup = 'main:consumer:p2p:choose_design'
const pageName = 'main:consumer:p2p:choose_design::'
/**
 * ThemeIcons are the circle buttons you click to select a theme/card
 */

function ThemeIcon({ iconImageUrl, changeSelectedCard, id, isSelected }) {
  const iconStyle = {
    backgroundImage: `url("${iconImageUrl}")`,
  }
  const iconClass = classNames({
    [css(styles.themeIcon)]: true,
    [css(styles.themeIconSelected)]: isSelected,
  })
  return (
    <button
      className={`${css(styles.buttonEl)} unit-test-theme-icon`}
      data-a11y-overpanel-focus-target={`theme-icon-${id}`}
      onClick={changeSelectedCard}
    >
      <div style={iconStyle} className={iconClass} />
    </button>
  )
}
ThemeIcon.propTypes = {
  iconImageUrl: PropTypes.string.isRequired,
  mainColor: PropTypes.string.isRequired,
  changeSelectedCard: PropTypes.func.isRequired,
  id: PropTypes.number.isRequired,
  isSelected: PropTypes.bool.isRequired,
}

export class NewThemeSelection extends React.Component {
  static propTypes = {
    displayName: PropTypes.string.isRequired,
    formattedAmount: PropTypes.string.isRequired,
    handleNextButton: PropTypes.func.isRequired, // should be used by parent to save theme, and close overpanel
    handleCancelButton: PropTypes.func.isRequired, // should be used by parent to clear theme, and close overpanel
    handleChangeNote: PropTypes.func.isRequired,
    themeList: PropTypes.array.isRequired,
    note: PropTypes.string,
    thumbnailUrl: PropTypes.string,
  }

  static defaultProps = {
    note: '',
    thumbnailUrl: '',
  }

  state = {
    selectedThemeIndex: 0,
    selectedCardIsFlipped: false,
  }

  changeSelectedCard = newIndex => {
    this.setState({
      selectedThemeIndex: newIndex,
      selectedCardIsFlipped: false,
    })
    // add analytics for theme selection
    fpti.trackLink('sel', pageGroup, pageName, 'link', null, {
      has_note: this.hasNote(),
      design: `theme:${newIndex}`,
    })
  }

  flipSelectedCard = () => {
    this.setState({
      selectedCardIsFlipped: !this.state.selectedCardIsFlipped,
    })
  }

  handleNoteClick = () => {
    fpti.trackLink('add_a_note', pageGroup, pageName, 'link', null, {
      has_note: this.hasNote(),
    })
    this.flipSelectedCard()
  }

  handleSubmitButton = () => {
    fpti.trackLink('save', pageGroup, pageName, 'link', null, {
      has_note: this.hasNote(),
    })
    return this.props.handleNextButton(
      this.props.themeList[this.state.selectedThemeIndex].id
    )
  }

  hasNote = () => {
    return this.props.note && this.props.note.length > 0
  }

  /**
   * Get the styles for the wrapper element that wraps both card front and card back
   */
  getCardWrapperStyles = currentIndex => {
    const isSelectedCard = currentIndex === this.state.selectedThemeIndex
    return {
      zIndex: isSelectedCard && 1000, // required for IE — hidden cards can block clicks if above in stack, ensure clickability
      pointerEvents: !isSelectedCard && 'none', // required for firefox — hidden cards can block clicks if above in stack, ensure clickability
      boxShadow: '0 3px 5px 0 rgba(0,0,0,0.24)',
    }
  }

  /**
   * Get the styles for the element serves as the front of the card.
   * Mainly used to flip the card. In most browsers flipping the wrapper altogether would be fine,
   * but in IE flipping each card face (giving the illusion that the wrapper is flipping) is required.
   */
  getCardFrontStyles = currentIndex => {
    return {
      backgroundColor: this.props.themeList[currentIndex].mainColor,
      backgroundImage: `url("${getThemeImage(
        this.props.themeList[currentIndex].id,
        'card'
      )}")`,
      backgroundSize: 'cover',
      display: this.state.selectedCardIsFlipped ? 'none' : 'inline-block',
    }
  }

  /**
   * Get the styles for the element serves as the back of the card.
   * Mainly used to flip the card. In most browsers flipping the wrapper altogether would be fine,
   * but in IE flipping each card face (giving the illusion that the wrapper is flipping) is required.
   */
  getCardBackStyles = () => {
    return {
      backgroundColor: `#4064AC; !important`,
      color: 'white !important',
      boxShadow: `0 2px 6px 0 rgba(0,0,0,0.24) !important`,
    }
  }

  /**
   * Get the gradient and opacity for the background based on the current theme
   */
  getBackgroundStyles = currentIndex => {
    const { selectedThemeIndex } = this.state
    return {
      opacity: currentIndex === selectedThemeIndex ? 1 : 0,
      background: this.props.themeList[currentIndex].gradient,
    }
  }

  renderMessageButtonText = () => {
    if (this.props.note && !this.state.selectedCardIsFlipped) {
      return i18n('selection.editMessage')
    }
    return this.state.selectedCardIsFlipped
      ? i18n('selection.saveMessage')
      : i18n('selection.addMessage')
  }

  handleCancelClick = () => {
    fpti.trackLink('cancel', pageGroup, pageName, 'link', null, {
      has_note: this.hasNote(),
    })
    this.props.handleCancelButton()
  }

  render() {
    const {
      note,
      displayName,
      thumbnailUrl,
      formattedAmount,
      handleChangeNote,
    } = this.props
    const { selectedThemeIndex } = this.state
    const { designer } = this.props.themeList[selectedThemeIndex] || {}

    return (
      <div ref={c => (this.containerEl = c)} className={css(styles.wrapper)}>
        {/* foreground: given position to sit on backgrounds */}
        <div className={css(styles.foreground)}>
          <h2 className={css(styles.header)}>
            {i18n('selection.chooseTheme')}
          </h2>

          {/* "centerPiece" is the ThemeIcons, cards, and flip button */}
          <div className={css(styles.centerPiece)}>
            {/* the theme icon buttons */}
            <div className={css(styles.iconsWrapperBackground)}>
              <div className={css(styles.iconsWrapper)}>
                {this.props.themeList.map(({ id, mainColor }, i) => (
                  <ThemeIcon
                    key={i}
                    id={i}
                    changeSelectedCard={() => this.changeSelectedCard(i)}
                    iconImageUrl={getThemeImage(id, 'icon')}
                    mainColor={mainColor}
                    isSelected={selectedThemeIndex === i}
                  />
                ))}
              </div>
            </div>

            {designer && (
              <div>
                <p className={css(styles.attribution)}>
                  {i18n.html('selection.newAttribution', { designer })}
                </p>
              </div>
            )}

            {/* the stack of cards */}
            <div className={css(styles.cardGroupWrapper)}>
              {this.props.themeList.map(({ id }, i) => (
                <ThemeCard
                  key={id}
                  themeId={id}
                  cardWrapperStyle={this.getCardWrapperStyles(i)}
                  cardFrontStyle={this.getCardFrontStyles(i)}
                  cardBackStyle={this.getCardBackStyles()}
                  handleCardFrontClick={this.flipSelectedCard}
                  editableMessage={true}
                  isNoteFieldTabEnabled={
                    i === this.state.selectedThemeIndex &&
                    this.state.selectedCardIsFlipped
                  }
                  note={note}
                  handleChangeNote={handleChangeNote}
                  displayName={displayName}
                  formattedAmount={formattedAmount}
                  thumbnailUrl={thumbnailUrl}
                  footerThemeImageUrl={getThemeImage(id, 'footer')}
                  isNewDesign={true}
                />
              ))}
            </div>

            {/* the flip button */}
            <button
              className={`${css(styles.messageBtn)} vx_btn vx_btn-secondary`}
              onClick={this.handleNoteClick}
            >
              {this.renderMessageButtonText()}
            </button>
          </div>

          <button
            onClick={this.handleSubmitButton}
            data-nemo="gift-overpanel-next"
            className={`${css(styles.nextButton)} vx_btn vx_btn-primary`}
          >
            {i18n('selection.save')}
          </button>

          <button
            className={`${css(styles.cancelButton)} vx_btn-link`}
            onClick={this.handleCancelClick}
          >
            {i18n('selection.cancel')}
          </button>
        </div>
      </div>
    )
  }
}

export default connect(({ theme, user }) => ({
  themeList: theme.themeList,
}))(NewThemeSelection)
